import { useQuery } from '@apollo/client';
import {
  ContactSearchProfileCardFragment,
  GetContactProfileRatingQuery,
} from '../../../generated/graphql';
import {
  getSearchResultRating,
  shouldFetchProfileRating,
} from '../../../utils/search-ratings';
import getDepthTierLabel, { DepthTierLabel } from '../../../utils/depth-tier';
import { GET_CONTACT_PROFILE_RATING } from '../../contact-profile-card/contact-search-profile-card-with-fragment';

export interface ContactRating {
  averageRating: number;
  totalReviews: number;
}

export interface ContactRatingResult {
  /** Rating to display on the card, when the agent is eligible for one. */
  rating?: ContactRating;
  depthTier?: DepthTierLabel;
  reviewCount?: number;
  starRating?: number;
}

const useContactRating = (
  contact: ContactSearchProfileCardFragment,
): ContactRatingResult => {
  const { data: profileData, loading: profileLoading } =
    useQuery<GetContactProfileRatingQuery>(GET_CONTACT_PROFILE_RATING, {
      variables: { contactId: contact.id },
      skip: !contact.id || !shouldFetchProfileRating(contact.profileTier),
    });

  const profile = profileData?.contactByContactId;
  const profileReviewCount = profile?.recommendations?.numberOfReviews;
  const profileStarRating = profile?.recommendations?.overallStarRating;

  const { rating, totalReviews } =
    getSearchResultRating({
      searchRecentReviewCount: contact.reputation?.numberOfReviewsRecent,
      profileRating: profileStarRating,
      profileReviewCount,
      profileLoading,
      profileTier: contact.profileTier,
      depthTier: profile?.depthTier,
    }) ?? {};

  return {
    rating:
      rating === undefined || totalReviews === undefined
        ? undefined
        : { averageRating: rating, totalReviews },
    depthTier: getDepthTierLabel(contact.profileTier, profile?.depthTier),
    reviewCount: profileReviewCount ?? undefined,
    starRating: profileStarRating ?? undefined,
  };
};

export default useContactRating;
